import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import GuideShell, { StepCard, TipCallout, InfoRow } from './guides/GuideShell';

function AudienceGuideShareCard() {
  const url = `${window.location.origin}/guides/audience-prep`;
  const [flash, setFlash] = useState(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setFlash('copied');
      setTimeout(() => setFlash(null), 1800);
    } catch (_) {
      setFlash('failed');
      setTimeout(() => setFlash(null), 2200);
    }
  };
  const share = async () => {
    // Prefer native share sheet on mobile (falls back to copy elsewhere)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Clanster — Audience Prep Guide',
          text: "Here's the 4-step walkthrough so you're ready for my launch:",
          url,
        });
        setFlash('shared');
        setTimeout(() => setFlash(null), 1800);
        return;
      } catch (_) { /* user cancelled — fall through to copy */ }
    }
    copy();
  };
  return (
    <div className="guide-share-card" data-testid="audience-share-card">
      <div className="guide-share-emoji">👥</div>
      <div className="guide-share-body">
        <div className="guide-share-title">Audience Prep Guide</div>
        <div className="guide-share-sub">
          Share this with your fans so they arrive launch-ready. It walks them through signup, deposit, bookmark + the reminder bell.
        </div>
        <div className="guide-share-url" data-testid="audience-share-url">{url}</div>
        <div className="guide-share-actions">
          <button
            type="button"
            className={`guide-share-btn is-primary${flash === 'copied' ? ' is-flash' : ''}`}
            onClick={copy}
            data-testid="audience-share-copy-btn"
          >
            {flash === 'copied' ? '✓ Link copied' : flash === 'failed' ? '✕ Copy failed' : '📋 Copy link'}
          </button>
          {typeof navigator !== 'undefined' && navigator.share && (
            <button
              type="button"
              className="guide-share-btn"
              onClick={share}
              data-testid="audience-share-native-btn"
            >
              📤 Share…
            </button>
          )}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="guide-share-btn"
            data-testid="audience-share-open-btn"
          >
            🔗 Open in new tab →
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * /guides/creator-prep — public, no auth required.
 *
 * Long-form illustrated walkthrough of the Creator Playbook. Linked from the
 * playbook email so recipients land on a page that expands every step with
 * screenshots + context. Public so admins can share the URL with prospects
 * before they sign up.
 */
export default function GuideCreatorPrep() {
  return (
    <GuideShell
      slug="creator-prep"
      eyebrow="For creators"
      title="Your Clanster launch playbook 🚀"
      subtitle="Everything you need to nail your first launch — from onboarding to post-launch engagement. Read it once, share what you need with your fans. Permanently pinned under Help Center so you can reread it any time."
      accent="creator"
    >
      <StepCard number={1} title="Finish onboarding & verify your socials">
        <p>
          Sign up (~5 min), upload an avatar and banner, and connect your <b>X</b> or{' '}
          <b>YouTube</b> for the verified badge. Only verified creators can <b>Schedule Launch</b>{' '}
          instead of launching instantly.
        </p>
        <p>
          Open the <b>Create Clan wizard</b> and choose your launch mode:
        </p>
        <InfoRow>
          <div>
            <b>Launch Now</b> — Bits are tradable the moment you finish the wizard.
          </div>
          <div>
            <b>Schedule Launch</b> — pick a date/time (min 1h ahead, max 30d). Sets a public
            countdown + a 🔔 Reminder Bell so fans can subscribe to launch-time alerts.
          </div>
        </InfoRow>
        <figure className="guide-figure">
          <img src="/guides/creator-1.png" alt="Clanster home page — Discover Creators grid" loading="lazy" />
          <figcaption>Your Home once onboarding is done — this is what your fans will see when they sign up.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={2} title="Grab your personal referral link">
        <p>
          Profile menu → <b>Refer Friends</b>. Copy your link — you'll use it in every
          social post from here on.
        </p>
        <TipCallout>
          Every fan who signs up through your link earns you <b>1% of every trade they ever
          make — forever</b>. Instant payout to your wallet on every trade, no vesting.
        </TipCallout>
        <figure className="guide-figure">
          <img src="/guides/creator-2.png" alt="Refer Friends page showing your invite code, people joined, and commission earned" loading="lazy" />
          <figcaption>The Refer Friends page — invite code, tally, and one-tap copy-link.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={3} title="Post your referral link everywhere">
        <p>
          Put it in the bio of every platform where your audience lives:
        </p>
        <ul>
          <li>X (Twitter) — pinned tweet + bio link</li>
          <li>YouTube — channel description + community post</li>
          <li>Instagram — bio link</li>
          <li>Facebook, TikTok, Snapchat — bio / pinned post</li>
          <li>Discord announcements</li>
          <li>Newsletter footer</li>
          <li>Anywhere else you already talk to your fans</li>
        </ul>
        <p>
          Widest funnel wins. You want everyone to have a chance to see it before launch.
        </p>
        <figure className="guide-figure">
          <img src="/guides/creator-3.png" alt="Social platforms where creators post their referral link — Facebook, Instagram, Snapchat, TikTok, YouTube, X" loading="lazy" />
          <figcaption>Post your referral link everywhere your audience already lives.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={4} title="Prime your audience — 3 asks">
        <p>Tell your fans to do three things:</p>
        <ol>
          <li>
            <b>Sign up</b> with your referral link
          </li>
          <li>
            <b>Load funds</b> into their Clanster wallet — crypto deposit, minimum $10.
            Deposits usually settle in a few minutes.
          </li>
          <li>
            <b>Bookmark your profile</b> + hit the 🔔 <b>Reminder Bell</b> on your Clan page.
            They'll get an email and push notification the exact moment trading opens.
          </li>
        </ol>
        <TipCallout tone="warn">
          <b>Early buyers grab the cheapest Bits — set expectations up front.</b> But also
          tell your fans: <i>later buyers still get the full clan experience</i> (private
          Feed, Live, Chat, Perks, Milestone Airdrops, Bounties). Coming in late doesn't
          lock anyone out — it just means a slightly higher entry price.
        </TipCallout>
        <figure className="guide-figure">
          <img src="/guides/creator-4.png" alt="Public Launches page — upcoming Clans with reminder bells" loading="lazy" />
          <figcaption>The public Launches page — fans hit 🔔 to be notified the exact moment your Clan goes live.</figcaption>
        </figure>
        <p>
          Share the fan-facing walkthrough with your community:
        </p>
        <AudienceGuideShareCard />
      </StepCard>

      <StepCard number={5} title="The launch moment">
        <p>
          Trading opens automatically at your scheduled time (or instantly if you picked{' '}
          <b>Launch Now</b>). What to do the first 30 minutes:
        </p>
        <ul>
          <li>Post "we're live!" across every channel with your Clan link</li>
          <li>
            Welcome your first buyers in <b>Clan Chat</b> — thank them by name
          </li>
          <li>
            Watch the curve climb in real time. The first 10-20 Bits move fastest — early
            momentum sets the tone
          </li>
        </ul>
        <figure className="guide-figure">
          <img src="/guides/creator-5.png" alt="A live Clan page showing current Bit price, holders, and trading chart" loading="lazy" />
          <figcaption>Your live Clan page — real-time price, holders, and the Bookmark + 🔔 buttons your fans use to save you.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={6} title="Where creator income actually compounds">
        <p>
          Minting is Day One. The bigger revenue comes from{' '}
          <b>the 5% royalty on every trade</b> your Bits generate over the next 90 days.
          The bonding curve is designed to steepen as your Clan grows, so every early
          holder you activate — and every new fan they refer — compounds your per-trade
          royalty for you. Active creators earn <b>5–10× more</b> than passive ones.
          Here's what "active" looks like:
        </p>
        <ul>
          <li>
            <b>Post to your private Feed</b> a few times per week — holder-only content
            drives repeat visits
          </li>
          <li>
            <b>Go Live for holders</b> — nothing spikes trading volume like a live drop or
            AMA
          </li>
          <li>
            <b>Drop Perks</b> — call time, custom art, presets, shoutouts. 100% of the Points
            go to you, no platform cut
          </li>
          <li>
            <b>Arm Bounties</b> — quests for your fans (record a TikTok, design a poster,
            reply to my post) that pay out Points on completion
          </li>
          <li>
            <b>Set Milestone Airdrops</b> — reward your top holders when the Clan hits a
            fill target
          </li>
        </ul>
        <TipCallout tone="win">
          The community shows up because they own a piece of you. Your job is to make owning
          that piece feel meaningful.
        </TipCallout>
        <figure className="guide-figure">
          <img src="/guides/creator-6.png" alt="A live Clan page with the Feed, Live, Chat, Perks, Bounties, and Members tabs — where creator engagement compounds" loading="lazy" />
          <figcaption>Your Clan's holder-only tabs — where the 5% royalty engine actually compounds.</figcaption>
        </figure>
      </StepCard>

      <div className="guide-cta">
        <div className="guide-cta-title">Ready to launch?</div>
        <div className="guide-cta-sub">
          Everything above is inside the app once you sign up.
          This guide is permanently pinned under <b>Help Center</b> — reread it any time.
        </div>
        <Link to="/signup" className="guide-cta-btn" data-testid="guide-signup-btn">
          Open Clanster →
        </Link>
      </div>
    </GuideShell>
  );
}
